import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getSettings } from "@/lib/publicContent";
import { hasAnalyticsConsent, COOKIE_CONSENT_CHANGED } from "@/components/CookieConsent";

// GA4 Measurement ID format, e.g. G-XXXXXXXXXX
const GA4_ID_RE = /^G-[A-Z0-9]{4,}$/i;

/**
 * DebugView helper: enable with `?ga_debug=1` (persists), disable with `?ga_debug=0`.
 * When on, events are marked `debug_mode:true` so they appear in GA4 Admin → DebugView
 * in real time — useful to confirm which property actually receives the hits.
 */
function ga4DebugEnabled(): boolean {
  try {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("ga_debug") === "1") {
      localStorage.setItem("ga_debug", "1");
      return true;
    }
    if (sp.get("ga_debug") === "0") {
      localStorage.removeItem("ga_debug");
      return false;
    }
    return localStorage.getItem("ga_debug") === "1";
  } catch {
    return false;
  }
}

function loadGa4(id: string): void {
  if (document.getElementById("ga4-src")) return;
  const s = document.createElement("script");
  s.id = "ga4-src";
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(s);
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  w.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer.push(arguments);
  };
  w.gtag("js", new Date());
  // Manual page_view tracking (so React Router navigations are counted once).
  w.gtag("config", id, { send_page_view: false, debug_mode: ga4DebugEnabled() });
}

/**
 * Analytics orchestrator. Loads Google Analytics 4 ONLY when:
 *  - a valid GA4 Measurement ID is configured in the CMS settings, AND
 *  - the user has accepted analytics cookies via the cookie banner.
 * Tracks every page, including client-side React Router navigations.
 *
 * Designed to be extended: add other providers by reading additional keys
 * from `settings.integrations` and initialising them here.
 */
export const Analytics: React.FC = () => {
  const location = useLocation();
  const [gaId, setGaId] = useState<string | null>(null);
  const [consent, setConsent] = useState<boolean>(hasAnalyticsConsent());
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    getSettings()
      .then((s) => {
        const id = (s.integrations?.ga4_measurement_id || "").trim();
        if (id && GA4_ID_RE.test(id)) setGaId(id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handler = () => setConsent(hasAnalyticsConsent());
    window.addEventListener(COOKIE_CONSENT_CHANGED, handler);
    return () => window.removeEventListener(COOKIE_CONSENT_CHANGED, handler);
  }, []);

  useEffect(() => {
    if (gaId && consent && !loaded) {
      loadGa4(gaId);
      setLoaded(true);
    }
  }, [gaId, consent, loaded]);

  // Send a page_view on first load (once GA is ready) AND on every subsequent
  // client-side navigation. `loaded` is state so the initial page_view fires
  // for the page the user is already on when consent is granted.
  useEffect(() => {
    if (!loaded || !gaId) return;
    const g = (window as any).gtag;
    if (typeof g === "function") {
      g("event", "page_view", {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
        ...(ga4DebugEnabled() ? { debug_mode: true } : {}),
      });
    }
  }, [location, gaId, loaded]);

  return null;
};
